import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useWatch, type Control } from "react-hook-form";
import type { ColumnDef } from "@tanstack/react-table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { X, Mail, Zap, Sun, CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useServerTable } from "@/hooks/useServerTable";
import { List } from "@/components/List";
import { SelectColumn } from "@/components/tableColumns/SelectColumn";
import { USER_BASE_COLUMNS } from "@/components/Users";
import { getTableSearchParam } from "@/utils/filters";
import { CUSTOM_FILTERS } from "@/config/list";
import { user as userApi, type User } from "@/endpoints/dryp/user";
import { COOLDOWN_OPTIONS, DELIVERY_MODE_LABELS, DELIVERY_MODE_DESCRIPTIONS, digestSendTimeLocal } from "../types";
import type { WizardFormData } from "./AlarmWizardStore";
import { useAlarmWizardStore } from "./useAlarmWizardStore";

const memberColumns: ColumnDef<User>[] = [SelectColumn<User>({ id: "id", mode: "multi" }), ...USER_BASE_COLUMNS];

const DELIVERY_MODE_CARDS: { value: Dryp.AlarmDeliveryMode; icon: typeof Zap }[] = [
	{ value: "immediate", icon: Zap },
	{ value: "daily_digest", icon: Sun },
	{ value: "weekly_digest", icon: CalendarDays },
];

type TierSeverity = "critical" | "warning";

const TIER_STYLES: Record<
	TierSeverity,
	{
		eyebrow: string;
		eyebrowText: string;
		container: string;
		selectedCard: string;
		selectedChip: string;
		selectedLabel: string;
	}
> = {
	critical: {
		eyebrow: "Critical",
		eyebrowText: "text-destructive/80",
		container: "border-destructive/25 bg-destructive/[0.04] dark:border-destructive/30 dark:bg-destructive/[0.07]",
		selectedCard: "border-destructive bg-destructive/5",
		selectedChip: "bg-destructive/10 text-destructive",
		selectedLabel: "text-destructive",
	},
	warning: {
		eyebrow: "Warning",
		eyebrowText: "text-amber-700/90 dark:text-amber-400/90",
		container: "border-amber-300/50 bg-amber-50/40 dark:border-amber-500/25 dark:bg-amber-500/[0.05]",
		selectedCard: "border-amber-400 bg-amber-50/60 dark:border-amber-500/40 dark:bg-amber-500/[0.08]",
		selectedChip: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
		selectedLabel: "text-amber-700 dark:text-amber-300",
	},
};

const DeliveryModePicker = ({
	value,
	severity,
	onChange,
}: {
	value: Dryp.AlarmDeliveryMode | null | undefined;
	severity: TierSeverity;
	onChange: (mode: Dryp.AlarmDeliveryMode) => void;
}) => {
	const styles = TIER_STYLES[severity];
	return (
		<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
			{DELIVERY_MODE_CARDS.map(card => {
				const isSelected = value === card.value;
				return (
					<button
						key={card.value}
						type="button"
						onClick={() => {
							if (value === card.value) return;
							onChange(card.value);
						}}
						className={cn(
							"flex flex-col gap-2 rounded-lg border-2 p-4 text-left transition-all cursor-pointer",
							isSelected ? styles.selectedCard : "border-border bg-background/60 hover:bg-accent/50",
						)}
					>
						<div className="flex items-center gap-2.5">
							<div
								className={cn(
									"flex items-center justify-center size-7 rounded-md shrink-0",
									isSelected ? styles.selectedChip : "bg-muted text-muted-foreground",
								)}
							>
								<card.icon className="size-4" />
							</div>
							<div className={cn("font-semibold text-sm", isSelected && styles.selectedLabel)}>
								{DELIVERY_MODE_LABELS[card.value]}
							</div>
						</div>
						<div className="text-xs text-muted-foreground leading-relaxed">
							{DELIVERY_MODE_DESCRIPTIONS[card.value]}
							{card.value !== "immediate" && ` (${digestSendTimeLocal()} your time)`}
						</div>
					</button>
				);
			})}
		</div>
	);
};

const DeliveryTierSection = ({
	control,
	name,
	severity,
	label,
	description,
}: {
	control: Control<WizardFormData>;
	name: "criticalDeliveryMode" | "warningDeliveryMode";
	severity: TierSeverity;
	label: string;
	description: string;
}) => {
	const styles = TIER_STYLES[severity];
	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem className={cn("space-y-3 rounded-lg border p-3", styles.container)}>
					<div>
						<span className={cn("text-[11px] font-semibold uppercase tracking-wide", styles.eyebrowText)}>
							{styles.eyebrow}
						</span>
						<Label className="mt-1 block text-sm font-medium">{label}</Label>
						<p className="text-xs text-muted-foreground mt-0.5">{description}</p>
					</div>
					<FormControl>
						<DeliveryModePicker value={field.value} severity={severity} onChange={field.onChange} />
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
};

const RecipientsTable = observer(
	({ value, onChange }: { value: Dryp.AlarmRecipient[]; onChange: (recipients: Dryp.AlarmRecipient[]) => void }) => {
		const usersApi = useAsyncStateStore(userApi.getUsers, { debounce: 300 });

		const { table, tableState } = useServerTable({
			columns: memberColumns,
			data: usersApi.data?.items || [],
			fetchFn: params =>
				usersApi.fetch(
					{ pageNumber: params.page, pageSize: params.pageSize },
					params.sort,
					getTableSearchParam(params?.filters),
				),
			initialValues: { page: 0, pageSize: 5 },
		});

		useEffect(() => {
			const internalIds = value
				.map(recipient => recipient.userId)
				.filter((userId): userId is Dryp.UserId => !!userId);
			if (internalIds.length === 0) return;
			tableState.setRowSelection(Object.fromEntries(internalIds.map(userId => [userId, true])));
			// Hydrate once on mount — subsequent changes flow from the table into the form.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const selectedUserIds = useMemo(
			() =>
				Object.keys(tableState.rowSelectionSnapshot).filter(
					id => tableState.rowSelectionSnapshot[id],
				) as Dryp.UserId[],
			[tableState.rowSelectionSnapshot],
		);

		useEffect(() => {
			const currentInternalIds = value
				.filter(recipient => recipient.userId)
				.map(recipient => recipient.userId as Dryp.UserId);

			// Hydration race: on mount, the hydration effect above schedules a
			// rowSelection update, but the first render of this effect still sees
			// selectedUserIds as []. Without this guard we'd wipe the internals
			// from `value` for one render before they get added back in.
			if (currentInternalIds.length > 0 && selectedUserIds.length === 0) return;

			const externals = value.filter(recipient => !recipient.userId);
			const internals: Dryp.AlarmRecipient[] = selectedUserIds.map(userId => ({ userId }));
			const next = [...internals, ...externals];
			if (
				currentInternalIds.length === selectedUserIds.length &&
				currentInternalIds.every(id => selectedUserIds.includes(id))
			) {
				return;
			}
			onChange(next);
		}, [selectedUserIds, value, onChange]);

		return (
			<List
				table={table}
				layout={{
					loading: usersApi.loading,
					className: "[&>div:first-child]:!py-1",
					tableClassName: "text-xs [&_td]:py-1 [&_th]:h-8 [&_th]:text-xs",
				}}
				pagination={{
					total: usersApi.data?.query?.pagination?.totalItems || 0,
					className: "!py-2 text-xs [&>div]:!py-0",
				}}
				toolbar={{ filterKey: CUSTOM_FILTERS.search, hideToolbarActions: true }}
			/>
		);
	},
);

export const StepRecipients = observer(() => {
	const state = useAlarmWizardStore();
	const recipients = useWatch({ control: state.form.control, name: "recipients" }) ?? [];
	const criticalDeliveryMode = useWatch({ control: state.form.control, name: "criticalDeliveryMode" });
	const warningDeliveryMode = useWatch({ control: state.form.control, name: "warningDeliveryMode" });
	const warningThreshold = useWatch({ control: state.form.control, name: "warningThreshold" });
	const hasWarningTier = warningThreshold != null;
	const showCooldown =
		criticalDeliveryMode === "immediate" || (hasWarningTier && warningDeliveryMode === "immediate");

	useEffect(() => {
		if (!showCooldown) state.form.setValue("cooldownMinutes", 0, { shouldDirty: true });
	}, [showCooldown, state.form]);

	const [externalName, setExternalName] = useState("");
	const [externalEmail, setExternalEmail] = useState("");

	const trimmedName = externalName.trim();
	const trimmedEmail = externalEmail.trim();
	const canAddExternal =
		trimmedName.length > 0 &&
		trimmedEmail.includes("@") &&
		!recipients.some(recipient => recipient.email === trimmedEmail);

	const addExternalRecipient = () => {
		if (!canAddExternal) return;
		const newRecipient: Dryp.AlarmRecipient = { name: trimmedName, email: trimmedEmail };
		state.form.setValue("recipients", [...recipients, newRecipient], { shouldValidate: true });
		setExternalName("");
		setExternalEmail("");
	};

	const removeExternal = (email: string) => {
		state.form.setValue(
			"recipients",
			recipients.filter(recipient => recipient.userId || recipient.email !== email),
			{ shouldValidate: true },
		);
	};

	const externalRecipients = recipients.filter(recipient => !recipient.userId);

	return (
		<div className="space-y-5">
			<div>
				<h3 className="text-base font-semibold">Notification</h3>
				<p className="text-sm text-muted-foreground mt-0.5">Choose how and when notifications are delivered.</p>
			</div>

			<DeliveryTierSection
				control={state.form.control}
				name="criticalDeliveryMode"
				severity="critical"
				label="Delivery"
				description={
					hasWarningTier
						? "How critical alerts are delivered — immediately or bundled into a digest."
						: "Pick whether notifications fire immediately or are bundled into a digest."
				}
			/>

			{hasWarningTier && (
				<DeliveryTierSection
					control={state.form.control}
					name="warningDeliveryMode"
					severity="warning"
					label="Delivery"
					description="How earlier warning-level alerts are delivered. Often a digest while critical stays immediate."
				/>
			)}

			{showCooldown && (
				<FormField
					control={state.form.control}
					name="cooldownMinutes"
					render={({ field }) => (
						<FormItem className="space-y-1.5">
							<Label htmlFor="alarm-cooldown" className="text-sm font-medium">
								Cooldown
							</Label>
							<p className="text-xs text-muted-foreground">
								Minimum time before recipients are re-notified for the same alarm. Helps avoid alert
								fatigue when values hover near the threshold.
							</p>
							<FormControl>
								<Select
									value={field.value != null ? String(field.value) : undefined}
									onValueChange={value => field.onChange(Number(value))}
								>
									<SelectTrigger id="alarm-cooldown" className="w-full md:w-72">
										<SelectValue placeholder="Select a cooldown" />
									</SelectTrigger>
									<SelectContent>
										{COOLDOWN_OPTIONS.map(option => (
											<SelectItem key={option.value} value={String(option.value)}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			)}

			<FormField
				control={state.form.control}
				name="recipients"
				render={({ field }) => (
					<FormItem className="space-y-3">
						<div>
							<Label className="text-sm font-medium">Recipients</Label>
							<p className="text-xs text-muted-foreground mt-0.5">
								Choose who gets notified when this alarm triggers.
							</p>
						</div>
						<div className="space-y-1.5">
							<Label className="text-xs font-medium text-muted-foreground">Team members</Label>
							<RecipientsTable value={field.value ?? []} onChange={field.onChange} />
						</div>

						<div className="space-y-1.5">
							<div>
								<Label className="text-xs font-medium text-muted-foreground">External recipients</Label>
								<p className="text-xs text-muted-foreground">
									Add people outside your organisation. Both name and email are required.
								</p>
							</div>
							<div className="flex gap-2">
								<Input
									type="text"
									placeholder="Name"
									value={externalName}
									onChange={e => setExternalName(e.target.value)}
									onKeyDown={e => {
										if (e.key === "Enter") {
											e.preventDefault();
											addExternalRecipient();
										}
									}}
									className="flex-1"
								/>
								<div className="relative flex-1">
									<Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
									<Input
										type="email"
										placeholder="email@example.com"
										value={externalEmail}
										onChange={e => setExternalEmail(e.target.value)}
										onKeyDown={e => {
											if (e.key === "Enter") {
												e.preventDefault();
												addExternalRecipient();
											}
										}}
										className="pl-9"
									/>
								</div>
								<Button
									type="button"
									variant="outline"
									onClick={addExternalRecipient}
									disabled={!canAddExternal}
								>
									Add
								</Button>
							</div>
							{externalRecipients.length > 0 && (
								<div className="flex flex-wrap gap-1.5">
									{externalRecipients.map(recipient => (
										<Badge
											key={recipient.email}
											variant="secondary"
											className="gap-1.5 pl-2 pr-1 py-0.5"
										>
											<span className="text-xs">
												<span className="font-medium">{recipient.name}</span>
												<span className="text-muted-foreground"> · {recipient.email}</span>
											</span>
											<button
												type="button"
												onClick={() => removeExternal(recipient.email!)}
												className="rounded hover:text-destructive transition-colors"
											>
												<X className="size-3" />
											</button>
										</Badge>
									))}
								</div>
							)}
						</div>
					</FormItem>
				)}
			/>
		</div>
	);
});
